import constants from '@/constants'

const MAIN = constants.DOMAINS.MAIN
const SECOND = constants.DOMAINS.SECOND

/**
 * Which zone this layer is serving, and how to move a URL between the two.
 *
 * The backend never mints a RU media URL and cannot: TaskExecuted is a queued
 * broadcast, so its payload is built by a worker, where `is_ru_context()` is
 * false by construction - it requires php_sapi_name() === 'fpm-fcgi'. Every
 * creative therefore arrives pointing at the global CDN, and the browser is the
 * only thing that can send it to the mirror instead.
 */

/**
 * True for the RU zone.
 *
 * This used to be `hostname === 'layer.apiqlive.ru'`, an exact match against one
 * name. Any other RU entry point - a second subdomain, a staging name, the host
 * reached with a trailing dot - fell through to the global CDN silently, which
 * is the failure this whole path exists to avoid.
 */
export function isRuHostname (hostname) {
  if (!hostname) return false

  const host = String(hostname).replace(/\.$/, '').toLowerCase()

  return host === SECOND || host.endsWith('.' + SECOND)
}

/** `cdn.apiq.live` -> `cdn.apiqlive.ru`, and the bare apex too. Anything else is left alone. */
export function ruHostname (hostname) {
  if (!hostname) return hostname

  const host = String(hostname).toLowerCase()

  if (host === MAIN) return SECOND

  return host.endsWith('.' + MAIN) ? host.slice(0, -MAIN.length) + SECOND : host
}

/** The other direction, for the fallback. */
export function globalHostname (hostname) {
  if (!hostname) return hostname

  const host = String(hostname).toLowerCase()

  if (host === SECOND) return MAIN

  return host.endsWith('.' + SECOND) ? host.slice(0, -SECOND.length) + MAIN : host
}

/**
 * Move a full URL into the RU zone.
 *
 * Parsed rather than string-replaced, and that is the point of this function.
 * The old `url.replace('apiq.live', 'apiqlive.ru')` rewrote the FIRST occurrence
 * anywhere in the string - so a signed URL carrying the domain in a query
 * parameter or a path segment came out corrupted, and the corruption looked
 * exactly like a creative that will not load.
 *
 * A URL that cannot be parsed is returned untouched: a broken rewrite is worse
 * than no rewrite, because the original at least resolves somewhere.
 */
export function ruUrl (raw) {
  return mapUrl(raw, ruHostname)
}

/**
 * The same URL in the other zone - the RU mirror for a global URL, the global
 * CDN for a mirrored one. Used once per element when the first load fails, so a
 * zone that is briefly unreachable costs a retry rather than a blank frame.
 */
export function alternateUrl (raw) {
  return mapUrl(raw, (host) => (isRuHostname(host) ? globalHostname(host) : ruHostname(host)))
}

function mapUrl (raw, mapHost) {
  if (!raw) return raw

  let url

  try {
    url = new URL(raw, window.location.origin)
  } catch (e) {
    return raw
  }

  const mapped = mapHost(url.hostname)

  if (!mapped || mapped === url.hostname) return raw

  url.hostname = mapped

  return url.href
}

export default { isRuHostname, ruHostname, globalHostname, ruUrl, alternateUrl }
